import React, {useEffect, useState} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {asyncLoop, newUuid, sleep} from "utilities/helperFunctions";
import {useDispatch, useSelector} from "react-redux";
import {Storage} from "aws-amplify";
import config from "configuration/settings.js";
import {useImageCompressor} from "utilities/fileUtilities";
import usePrevious from "hooks/usePrevious";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import Container from "connectui/component/layout/Container";
import LoadingBar from "connectui/component/display/LoadingBar";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";

const useStyles = (theme) =>
  buildStyles({
    imgContainer: {
      width: "50px",
      height: "50px",
      borderRadius: `${theme.radius.sm}px`,
      position: "relative",
      cursor: "pointer",
      "&:hover .x": {
        width: "18px",
        height: "18px",
        opacity: 1,
      },
    },
    xIcon: {
      width: 0,
      height: 0,
      opacity: 0,
      borderRadius: "50%",
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadows.sm,
      position: "absolute",
      cursor: "pointer",
      top: "-9px",
      right: "-9px",
      transition: "opacity 0.15s",
      padding: theme.spacing(0.5),
    },
    img: {
      objectFit: "cover",
      width: "50px",
      height: "50px",
      borderRadius: `${theme.radius.sm}px`,
    },
  });

const AttachmentsInput = ({
  inputRef,
  attachedImages,
  setAttachedImages,
  guestId,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.defaultReducer.current_user);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [progressObj, setProgressObj] = useState({loaded: 0, total: 100});
  const [compressEnabled, setCompressEnabled] = useState(false);
  const prevGuestId = usePrevious(guestId);

  useImageCompressor({
    srcImgs:
      !!compressEnabled && filesToUpload[0] === "upload"
        ? filesToUpload.slice(1)
        : [],
    onSuccess: (outputImgs) => onCompressSuccess(outputImgs),
  });

  useEffect(() => {
    if (prevGuestId !== guestId) {
      if (attachedImages.length) {
        // Remove attachments when switching conversations
        removeImages(attachedImages);
      }
      setAttachedImages([]);
    }
  }, [guestId, attachedImages]);

  useEffect(() => {
    if (!filesToUpload.length) return;
    if (filesToUpload[0] === "upload") {
      // Start compression
      sleep(250).then(() => {
        setLoaderOpen(true);
        setCompressEnabled((prev) => true);
      });
    }
  }, [filesToUpload]);

  const onCompressSuccess = (outputImgs) => {
    setCompressEnabled(false);
    setFilesToUpload([]);
    handleUploadImages(outputImgs);
  };

  // Upload images to S3
  const handleUploadImages = async (files) => {
    const newLinks = [];
    const totalProgress = {
      current: 0,
      totalSize: files.reduce((acc, curr) => acc + curr.size, 0),
    };
    setProgressObj((prev) => ({loaded: 0, total: totalProgress.totalSize}));

    await asyncLoop(files, async (item, index) => {
      const newId = newUuid();
      const imageName = `${currentUser}/messages/${guestId}/attachment-${newId}`;
      if (index > 0) {
        totalProgress.current += files[index - 1].size;
      }
      await Storage.put(imageName, item, {
        contentType: item.type,
        progressCallback(progress) {
          setProgressObj((prev) => ({
            ...prev,
            loaded: totalProgress.current + progress.loaded,
          }));
        },
      });
      newLinks.push({
        id: newId,
        url: `${config.s3.URL}${imageName}`,
        name: imageName,
      });
    });

    setAttachedImages((prev) => prev.concat(newLinks));
    setLoaderOpen(false);
  };

  // Remove unused attachments
  const removeImages = (imgs) => {
    if (imgs.length) {
      for (let index = 0; index < imgs.length; index++) {
        const img = imgs[index];
        Storage.remove(img.name);
      }
    }
  };

  const handleError = (errorText) => {
    dispatch(
      openGeneralError({
        open: true,
        message: "Failed to attach image",
        disableSeverity: true,
        subtitle: errorText,
        onClose: () => dispatch(closeGeneralError()),
      }),
    );
  };

  const validateFile = (file) => {
    return new Promise((resolve) => {
      const re = /^image\/.+$/;
      if (!re.test(file.type)) {
        handleError("Invalid file. Only images are allowed.");
      } else {
        setFilesToUpload((prev) => [...prev, file]);
      }
      resolve(true);
    });
  };

  const loadImages = async (files) => {
    // Prepare files to upload
    await asyncLoop(files, async (f) => {
      await validateFile(f);
    });

    // Files ready to upload
    setFilesToUpload((prev) => {
      if (!prev.length) return prev;
      else return ["upload", ...prev];
    });
    setProgressObj({loaded: 0, total: 100});
  };

  return (
    <>
      {loaderOpen && (
        <Container
          style={{
            width: "100%",
            padding: theme.spacing(0, 4),
            marginBottom: theme.spacing(1),
          }}
        >
          <LoadingBar
            progress={Math.ceil((progressObj.loaded * 100) / progressObj.total)}
          />
        </Container>
      )}
      {!!attachedImages.length && !loaderOpen && (
        <FlexContainer
          alignItems="center"
          gap={theme.spacing(2)}
          sx={{
            width: "100%",
            overflow: "auto hidden",
            padding: theme.spacing(2.5, 4, 0.5),
          }}
        >
          {attachedImages.map((img, i) => (
            <Container key={img.id} className={classes.imgContainer}>
              <SvgIcon
                component={ICONS["small-x"]}
                className={`${classes.xIcon} x`}
                onClick={() => {
                  setAttachedImages((prev) =>
                    prev.filter((attached) => attached.id !== img.id),
                  );
                  removeImages([img]);
                }}
              />
              <img
                key={img.id}
                src={img.url}
                alt={`Attachment ${i + 1}`}
                className={classes.img}
              />
            </Container>
          ))}
        </FlexContainer>
      )}
      <FlexContainer className={classes.container}>
        <input
          id="image-attachment"
          ref={inputRef}
          style={{display: "none"}}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => loadImages(e.target.files)}
        />
      </FlexContainer>
    </>
  );
};

export default AttachmentsInput;
